import React, { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Layout } from '@/components/layout'
import { ProtectedRoute } from '../guards/ProtectedRoute'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { isSubscriptionsEnabled } from '@/utils/featureFlags'
import { isOrganizationOwner } from '@/utils/permissions'
import { organizationService } from '@/services/organizationService'
import FirstTimeSetupModal from '@/components/onboarding/FirstTimeSetupModal'
import CommercializationAnnouncementModal from '@/components/announcements/CommercializationAnnouncementModal'
import { useScrapePolling } from '@/hooks/useScrapePolling'
import { useScoutPolling } from '@/hooks/useScoutPolling'

/**
 * ProtectedLayoutWrapper Component
 *
 * Wraps all protected routes with:
 * 1. ProtectedRoute guard (auth + onboarding + subscription checks)
 * 2. Layout component (Sidebar + TopBar + Footer)
 * 3. First-time setup modal (for organization owners)
 * 4. Outlet for nested route rendering
 *
 * This eliminates the need to repeat Layout wrapper for every protected route.
 */
export const ProtectedLayoutWrapper: React.FC = () => {
  const { user } = useAuthStore()
  const { sidebarCollapsed, setSidebarCollapsed } = useUIStore()
  const [showFirstTimeSetupModal, setShowFirstTimeSetupModal] = useState(false)
  useScrapePolling()
  useScoutPolling()

  // Check if first-time setup modal should be shown
  useEffect(() => {
    if (!user) {
      setShowFirstTimeSetupModal(false)
      return
    }

    const shouldShow =
      isSubscriptionsEnabled() &&
      isOrganizationOwner(user) &&
      user.onboardingStatus === 'completed' &&
      user.hasCompletedFirstTimeSetup === false

    setShowFirstTimeSetupModal(!!shouldShow)
  }, [user])

  // Handle first-time setup completion
  const handleFirstTimeSetupComplete = async () => {
    if (!user?.organizationId) return

    try {
      await organizationService.completeFirstTimeSetup(user.organizationId)
      setShowFirstTimeSetupModal(false)
      // Reload to refresh user data
      window.location.reload()
    } catch (error) {
      console.error('Failed to complete first-time setup:', error)
    }
  }

  return (
    <ProtectedRoute>
      <Layout
        sidebarCollapsed={sidebarCollapsed}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      >
        <Outlet />
      </Layout>

      {/* First-time setup modal (shown on top of any view) */}
      {showFirstTimeSetupModal && user?.organizationId && (
        <FirstTimeSetupModal
          isOpen={showFirstTimeSetupModal}
          onAccountAdded={handleFirstTimeSetupComplete}
          organizationId={user.organizationId}
        />
      )}

      {/* Commercialization announcement modal - uses localStorage to track if seen */}
      {!showFirstTimeSetupModal && <CommercializationAnnouncementModal />}
    </ProtectedRoute>
  )
}
